import { handleRequiredValues, isContribution, isExisting, isRollover } from './ira-add-funds-state-guards';
import {
	invalidIraType,
	iraAddFundsDisabled,
	iraAddFundsDisabledForUser,
	iraNotReadyToInvest,
	iraOnboardingIncomplete,
	missingAccountNumber
} from '@constants';
import { IraAddFundsRoute, IraFundingType, IraRequiredValue } from 'types/ira';
import { Route, RouteConfig } from 'vue-router';
import { flattenQueryParam } from '@src/utils/request';
import { ira } from '@store/modules/ira';
import { redirect } from '@src/utils/client';

const contributionRouteOrder: Array<IraAddFundsRoute> = [
	'ira-add-funds-funding',
	'ira-add-funds-amount',
	'ira-add-funds-plan',
	'ira-add-funds-drip',
	'ira-add-funds-max-contribution',
	'ira-add-funds-funding-source',
	'ira-add-funds-bank-account-new',
	'ira-add-funds-confirm',
	'ira-add-funds-agreements',
	'ira-add-funds-success'
];

const transferRouteOrder: Array<IraAddFundsRoute> = [
	'ira-add-funds-funding',
	'ira-add-funds-important-information',
	'ira-add-funds-transfer-type',
	'ira-add-funds-account-age',
	'ira-add-funds-account-not-eligible',
	'ira-add-funds-transfer-incompatible',
	'ira-add-funds-transfer-account-info',
	'ira-add-funds-transfer-amount',
	'ira-add-funds-plan',
	'ira-add-funds-drip',
	'ira-add-funds-confirm',
	'ira-add-funds-agreements',
	'ira-add-funds-success'
];
const existingRouteOrder: Array<IraAddFundsRoute> = [
	'ira-add-funds-funding',
	'ira-add-funds-transfer-amount',
	'ira-add-funds-plan',
	'ira-add-funds-drip',
	'ira-add-funds-confirm',
	'ira-add-funds-agreements',
	'ira-add-funds-success'
];
const rolloverRouteOrder: Array<IraAddFundsRoute> = [
	'ira-add-funds-funding',
	'ira-add-funds-important-information',
	'ira-add-funds-rollover-type',
	'ira-add-funds-rollover-source-account-type',
	'ira-add-funds-rollover-mixed-error',
	'ira-add-funds-transfer-incompatible',
	'ira-add-funds-rollover-method',
	'ira-add-funds-rollover-check-date',
	'ira-add-funds-rollover-check-not-eligible',
	'ira-add-funds-transfer-amount',
	'ira-add-funds-confirm',
	'ira-add-funds-agreements',
	'ira-add-funds-success'
];

const addFundsRouteOrder: Record<IraFundingType, Array<IraAddFundsRoute>> = {
	CONTRIBUTION: contributionRouteOrder,
	EXISTING: existingRouteOrder,
	TRANSFER: transferRouteOrder,
	ROLLOVER: rolloverRouteOrder
};

const getFlowStepIndex = (name: IraAddFundsRoute): number => {
	return addFundsRouteOrder[ira.fundingType].indexOf(name) + 1;
};

const metaConstructor = (route: Route): object => ({ step: getFlowStepIndex(route.name as IraAddFundsRoute) });

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const iraAddFundsRoutes: RouteConfig = {
	path: `/ira/add-funds`,
	name: 'ira-add-funds-start',
	beforeEnter: async (to, from, next) => {
		ira.UPDATE_FLOW('ADD-FUNDS');
		if (!ira.dataRetrieved) {
			try {
				const reitOfferingVintageId = flattenQueryParam(to.query.reitOfferingVintageId);

				await ira.getIraInfoForRepeatInvestment(reitOfferingVintageId);
				await next({ name: 'ira-add-funds-funding', replace: true });
			} catch (error) {
				if (error.response && error.response.data && error.response.status === 403) {
					switch (error.response.data.errorCode) {
						case missingAccountNumber:
							await next({ name: 'ira-start', replace: true });
							break;
						case iraOnboardingIncomplete:
						case iraNotReadyToInvest:
							ira.UPDATE_DATA_RETRIEVED(true);
							await next({ name: 'ira-add-funds-not-ready', replace: true });
							break;
						case invalidIraType:
							ira.UPDATE_DATA_RETRIEVED(true);
							await next({ name: 'ira-add-funds-invalid-ira', replace: true });
							break;
						case iraAddFundsDisabled:
						case iraAddFundsDisabledForUser:
							ira.UPDATE_DATA_RETRIEVED(true);
							await next({ name: 'ira-add-funds-disabled', replace: true });
							break;
						default:
							redirect('/account/overview');
							break;
					}
				} else {
					redirect('/account/overview');
				}
			}
		} else {
			await next();
		}
	},
	component: () => import(/* webpackChunkName: "ira-add-funds" */ '@views/ira/add-funds/ira-add-funds-start.vue'),
	children: [
		{
			path: `disabled`,
			name: 'ira-add-funds-disabled',
			meta: metaConstructor,
			component: () => import(/* webpackChunkName: "ira-start-disabled" */ '@views/ira/signup/ira-disabled.vue')
		},
		{
			path: `not-ready`,
			name: 'ira-add-funds-not-ready',
			meta: metaConstructor,
			component: () =>
				import(
					/* webpackChunkName: "ira-add-funds-not-ready" */ '@views/ira/add-funds/ira-add-funds-not-ready.vue'
				)
		},
		{
			path: `invalid-ira`,
			name: 'ira-add-funds-invalid-ira',
			meta: metaConstructor,
			component: () =>
				import(
					/* webpackChunkName: "ira-add-funds-invalid-ira" */ '@views/ira/add-funds/ira-add-funds-invalid-ira.vue'
				)
		},
		{
			path: `funding`,
			meta: metaConstructor,
			name: 'ira-add-funds-funding',
			component: () => import(/* webpackChunkName: "ira-start-funding" */ '@views/ira/signup/ira-funding.vue')
		},
		{
			path: `information`,
			meta: metaConstructor,
			name: 'ira-add-funds-important-information',
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['rolloverOrTransferFlow'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-important-info" */ '@views/ira/signup/ira-important-info.vue')
		},
		{
			path: `account-type`,
			meta: metaConstructor,
			name: 'ira-add-funds-transfer-type',
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['transferFlow'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-transfer-type" */ '@views/ira/signup/ira-transfer-type.vue')
		},
		{
			path: `rollover-type`,
			name: 'ira-add-funds-rollover-type',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['rolloverFlow'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-rollover-type" */ '@views/ira/signup/ira-rollover-type.vue')
		},
		{
			path: `rollover-account-type`,
			name: 'ira-add-funds-rollover-source-account-type',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['rolloverFlow', 'rolloverType'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-start-rollover-source-account-type" */ '@views/ira/signup/ira-rollover-source-account-type.vue'
				)
		},
		{
			path: `rollover-not-allowed`,
			name: 'ira-add-funds-rollover-mixed-error',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['rolloverFlow'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-start-rollover-mixed-error" */ '@views/ira/signup/ira-rollover-mixed-error.vue'
				)
		},
		{
			path: `rollover-method`,
			name: 'ira-add-funds-rollover-method',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['rolloverFlow', 'rolloverType', 'sourceAccountType'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-rollover-method" */ '@views/ira/signup/ira-rollover-method.vue')
		},
		{
			path: `rollover-date`,
			name: 'ira-add-funds-rollover-check-date',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = [
					'rolloverFlow',
					'rolloverType',
					'sourceAccountType',
					'rolloverMethodIndirect'
				];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-start-rollover-check-date" */ '@views/ira/signup/ira-rollover-check-date.vue'
				)
		},
		{
			path: `rollover-ineligible`,
			name: 'ira-add-funds-rollover-check-not-eligible',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = [
					'rolloverFlow',
					'rolloverType',
					'sourceAccountType',
					'rolloverMethodIndirect',
					'rolloverCheckDate'
				];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-start-rollover-check-not-eligible" */ '@views/ira/signup/ira-rollover-check-not-eligible.vue'
				)
		},
		{
			path: `account-age`,
			meta: metaConstructor,
			name: 'ira-add-funds-account-age',
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = [
					'transferFlow',
					'sourceAccountType',
					'simpleSourceAccountType'
				];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-account-age" */ '@views/ira/signup/ira-account-age.vue')
		},
		{
			path: `account-not-eligible`,
			meta: metaConstructor,
			name: 'ira-add-funds-account-not-eligible',
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = [
					'transferFlow',
					'sourceAccountType',
					'simpleSourceAccountType',
					'simpleIraFirstContributionDate',
					'simpleIraFirstContributionDateValidFalse'
				];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-start-account-not-eligible" */ '@views/ira/signup/ira-account-not-eligible.vue'
				)
		},
		{
			path: `incompatible`,
			name: 'ira-add-funds-transfer-incompatible',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['rolloverOrTransferFlow'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-add-funds-transfer-incompatible" */ '@views/ira/add-funds/ira-add-funds-transfer-incompatible.vue'
				)
		},
		{
			path: `previous-account`,
			name: 'ira-add-funds-transfer-account-info',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['transferFlow', 'sourceAccountType'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-start-transfer-account-info" */ '@views/ira/signup/ira-transfer-account-info.vue'
				)
		},
		{
			path: `transfer-amount`,
			name: 'ira-add-funds-transfer-amount',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				let requiredVals: Array<IraRequiredValue> = [];
				if (isRollover()) {
					requiredVals = ['rolloverFlow', 'sourceAccountType', 'rolloverMethod'];
				} else if (isExisting()) {
					requiredVals = ['transferOrExisting'];
				} else {
					requiredVals = [
						'transferFlow',
						'sourceAccountType',
						'sourceCustodianDtcInfo',
						'sourceAccountNumber'
					];
				}
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-transfer-amount" */ '@views/ira/signup/ira-transfer-amount.vue')
		},
		{
			path: `amount`,
			name: 'ira-add-funds-amount',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['contributionFlow'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () => import(/* webpackChunkName: "ira-start-amount" */ '@views/ira/signup/ira-amount.vue')
		},
		{
			path: `plan`,
			name: 'ira-add-funds-plan',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['addFundsPlanSelectionRequired'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () => import(/* webpackChunkName: "ira-start-plan" */ '@views/ira/signup/ira-plan.vue')
		},
		{
			path: `max-contribution`,
			name: 'ira-add-funds-max-contribution',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				if (from && from.name === 'ira-add-funds-amount') {
					next();
				} else {
					next({ name: 'ira-add-funds-amount' });
				}
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-amount" */ '@views/ira/signup/ira-max-contribution.vue')
		},
		{
			path: `funding-source`,
			name: 'ira-add-funds-funding-source',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['contributionFlow', 'selectedAmount'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-funding-source" */ '@views/ira/signup/ira-funding-source.vue')
		},
		{
			path: `bank-account-new`,
			name: 'ira-add-funds-bank-account-new',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				if (from && from.name === 'ira-add-funds-funding-source') {
					next();
				} else {
					next({ name: 'ira-add-funds-funding-source' });
				}
			},
			component: () =>
				import(
					/* webpackChunkName: "ira-start-bank-account-new" */ '@views/ira/signup/ira-bank-account-new.vue'
				)
		},
		{
			path: `drip`,
			name: 'ira-add-funds-drip',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				const requiredVals: Array<IraRequiredValue> = ['addFundsdripPreferenceRequired'];
				await handleRequiredValues(next, requiredVals);
			},
			component: () => import(/* webpackChunkName: "ira-start-drip" */ '@views/ira/signup/ira-drip.vue')
		},
		{
			path: `confirmation`,
			name: 'ira-add-funds-confirm',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				let requiredVals: Array<IraRequiredValue> = [];
				if (isContribution()) {
					requiredVals = [
						'selectedAmount',
						'selectedTaxYear',
						'plan',
						'dripPreference',
						'selectedPaymentMethod'
					];
				} else if (isExisting()) {
					requiredVals = ['selectedAmount', 'plan', 'dripPreference'];
				} else if (isRollover()) {
					requiredVals = ['sourceAccountType', 'rolloverMethod', 'selectedAmount'];
				} else {
					requiredVals = [
						'sourceAccountType',
						'sourceCustodianDtcInfo',
						'sourceAccountNumber',
						'selectedAmount',
						'plan',
						'dripPreference'
					];
				}
				await handleRequiredValues(next, requiredVals);
			},
			component: () => import(/* webpackChunkName: "ira-start-confirm" */ '@views/ira/signup/ira-confirm.vue')
		},
		{
			path: `agreements`,
			name: 'ira-add-funds-agreements',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				let requiredVals: Array<IraRequiredValue> = [];
				if (isRollover()) {
					requiredVals = ['rolloverLegacy'];
				} else {
					requiredVals = ['reitInvestmentOrderGroupId'];
				}
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-start-agreements" */ '@views/ira/signup/ira-agreements.vue')
		},
		{
			path: `success`,
			name: 'ira-add-funds-success',
			meta: metaConstructor,
			beforeEnter: async (to, from, next) => {
				let requiredVals: Array<IraRequiredValue> = [];
				if (isRollover()) {
					requiredVals = ['contactSet'];
				} else {
					requiredVals = ['reitInvestmentOrderGroupId', 'orderGroupAcknowledged'];
				}
				await handleRequiredValues(next, requiredVals);
			},
			component: () =>
				import(/* webpackChunkName: "ira-add-funds-success" */ '@views/ira/add-funds/ira-add-funds-success.vue')
		}
	]
};

export default iraAddFundsRoutes;
